import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PARTNER_LINKS = [
  { name: 'WALLIX', slug: 'wallix-pam', logo: '/assets/wallix.png', category: 'Privileged Access Management' },
  { name: 'Resecurity', slug: 'resecurity-threat-intelligence', logo: '/assets/Resecurity.png', category: 'Threat Intelligence' },
  { name: 'CyberRT', slug: 'cyber-rt', logo: '/assets/cyberrt.png', category: 'Compliance & GRC' },
  { name: 'Barracuda', slug: 'barracuda', logo: '/assets/barracuda.png', category: 'Email & Cloud Security' },
  { name: 'NSFOCUS', slug: 'nsfocus', logo: '/assets/nsfocus.png', category: 'Network & App Security' },
  { name: 'SecHard', slug: 'sechard', logo: '/assets/SecHard.png', category: 'Security Hardening' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!headerRef.current) return;
    setHeaderHeight(headerRef.current.offsetHeight);
  }, []);

  // `static: true` means the target is a real page served by the web server, not
  // a React route. It must be rendered as a plain <a> so the browser performs a
  // full page load — a react-router <Link> would navigate client-side, find no
  // matching route, and fall through to the 404 page.
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Services', path: '/services' },
    { name: 'Advisory', path: '/advisory' },
    { name: 'Partners', path: '/partners' },
    { name: 'Blog', path: '/blog/', static: true }
  ];

  return (
    <>
      <motion.header
        ref={headerRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass shadow-soft py-4' : 'bg-transparent py-4'
        }`}
        data-testid="header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center focus-ring rounded-lg"
              data-testid="logo-link"
            >
              <img
                src="/assets/logo.png"
                alt="Gulf Digital - Cybersecurity solutions in Saudi Arabia"
                className="h-16 md:h-20 w-auto"
                loading="eager"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
              {navLinks.map((link) => {
                const className = `px-4 py-2 rounded-lg text-sm font-medium transition-smooth focus-ring ${
                  location.pathname === link.path
                    ? 'text-brand-maroon bg-brand-maroon/10'
                    : 'text-foreground hover:text-brand-maroon hover:bg-warm-gray-50'
                }`;
                const testId = `nav-link-${link.name.toLowerCase().replace(' ', '-')}`;

                if (link.name === 'Partners') {
                  const isPartnersActive = location.pathname.startsWith('/partners');
                  return (
                    <DropdownMenu key={link.path}>
                      <DropdownMenuTrigger
                        className={`inline-flex items-center gap-1 ${className} ${
                          isPartnersActive ? 'text-brand-maroon bg-brand-maroon/10' : ''
                        }`}
                        data-testid={testId}
                      >
                        {link.name}
                        <ChevronDown className="w-3.5 h-3.5" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        sideOffset={12}
                        className="w-80 overflow-hidden rounded-xl border-warm-gray-200 p-0 shadow-soft-lg"
                      >
                        <div className="h-1 bg-gradient-to-r from-brand-maroon-dark via-brand-maroon to-brand-maroon-light" />
                        <p className="px-4 pt-3 pb-2 text-xs font-bold uppercase tracking-wider text-warm-gray-500">
                          Our Technology Partners
                        </p>
                        <div className="px-2 pb-2">
                          {PARTNER_LINKS.map((partner) => (
                            <DropdownMenuItem
                              key={partner.slug}
                              asChild
                              className="rounded-lg py-2 pl-2 pr-2 focus:bg-brand-maroon/10"
                            >
                              <Link to={`/partners/${partner.slug}`} className="flex cursor-pointer items-center gap-3">
                                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-warm-gray-50 border border-warm-gray-200">
                                  <img src={partner.logo} alt={partner.name} className="h-5 w-5 object-contain" />
                                </span>
                                <span className="flex flex-col">
                                  <span className="text-sm font-semibold text-foreground">{partner.name}</span>
                                  <span className="text-xs text-muted-foreground">{partner.category}</span>
                                </span>
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </div>
                        <DropdownMenuSeparator className="m-0 bg-warm-gray-200" />
                        <DropdownMenuItem asChild className="rounded-none py-3 px-4 justify-center font-semibold text-brand-maroon focus:bg-brand-maroon/10 focus:text-brand-maroon-dark">
                          <Link to="/partners" className="cursor-pointer">
                            View All Partners →
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }

                return link.static ? (
                  <a key={link.path} href={link.path} className={className} data-testid={testId}>
                    {link.name}
                  </a>
                ) : (
                  <Link key={link.path} to={link.path} className={className} data-testid={testId}>
                    {link.name}
                  </Link>
                );
              })}
            </nav>

            {/* Contact Button */}
            <div className="hidden lg:flex items-center space-x-3">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="focus-ring"
                data-testid="contact-button"
              >
                <Link to="/contact">
                  <Phone className="w-4 h-4 mr-2" />
                  Contact Sales
                </Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-warm-gray-100 focus-ring"
              aria-label="Toggle menu"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ top: headerHeight, maxHeight: `calc(100vh - ${headerHeight}px)` }}
            className="fixed left-0 right-0 z-40 lg:hidden glass-dark overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="container-custom py-6 space-y-2">
              {navLinks.map((link) => {
                const className = `block px-4 py-3 rounded-lg text-base font-medium transition-smooth ${
                  location.pathname === link.path
                    ? 'text-brand-maroon bg-brand-maroon/10'
                    : 'text-white hover:bg-white/10'
                }`;
                const testId = `mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`;

                if (link.name === 'Partners') {
                  const isPartnersActive = location.pathname.startsWith('/partners');
                  return (
                    <div key={link.path}>
                      <Link
                        to="/partners"
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-smooth ${
                          isPartnersActive ? 'text-brand-maroon bg-brand-maroon/10' : 'text-white hover:bg-white/10'
                        }`}
                        data-testid={testId}
                      >
                        {link.name}
                      </Link>
                      <div className="ml-4 space-y-1 border-l border-white/10 pl-4 mt-1">
                        {PARTNER_LINKS.map((partner) => (
                          <Link
                            key={partner.slug}
                            to={`/partners/${partner.slug}`}
                            className="block px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-smooth"
                          >
                            {partner.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return link.static ? (
                  <a key={link.path} href={link.path} className={className} data-testid={testId}>
                    {link.name}
                  </a>
                ) : (
                  <Link key={link.path} to={link.path} className={className} data-testid={testId}>
                    {link.name}
                  </Link>
                );
              })}
              <Link
                to="/contact"
                className="block px-4 py-3 rounded-lg text-base font-medium bg-brand-maroon text-white hover:bg-brand-maroon-dark transition-smooth"
              >
                Contact Sales
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
